import { useState } from 'react'

import {
  useAcknowledgeDrift,
  useDrift,
  useInventory,
  useInventoryFacet,
  type DriftRow,
  type InventoryFacet,
  type InventoryHost,
} from '../api/queries'
import { useTenant } from '../api/tenant'
import { Failure } from '../components/Failure'
import type { Session } from '../api/types'

const FACET_NAMES: Record<string, string> = {
  packages: 'Packages',
  users: 'Accounts',
  sudoers: 'Sudo rules',
  sshd: 'SSH config',
  sockets: 'Listening',
  suid: 'Setuid',
  os: 'OS release',
  containers: 'Containers',
}

const SEVERITY: Record<DriftRow['severity'], { word: string; colour: string }> = {
  critical: { word: 'critical', colour: 'var(--sev-critical)' },
  notable: { word: 'notable', colour: 'var(--sev-medium)' },
  info: { word: 'informational', colour: 'var(--sev-info)' },
}

/**
 * Inventory — what is on the estate, and what changed on it.
 *
 * Drift comes first and the host list second, which is the opposite of how an asset
 * screen is usually built. An inventory nobody diffs is a spreadsheet: the useful
 * question is not "what is installed on web01" but "what changed last night that
 * nobody asked for", and a screen that opens on three thousand package rows buries it.
 *
 * Two things are said in words rather than implied by an absence. A facet the agent
 * could not read is labelled unreadable, not shown as zero items — an unreadable
 * `/etc/sudoers` and a host with no sudo rules look identical in a count and mean
 * opposite things. And a stale facet says so, because an inventory nobody has
 * refreshed is exactly the input a compliance answer should not be built on.
 */
export function InventoryPage({
  tenants = [],
  session,
}: {
  tenants?: string[]
  session?: Session
}) {
  const [tenant] = useTenant(tenants)
  const [severity, setSeverity] = useState('')
  const [open, setOpen] = useState<{ hostname: string; facet: string } | null>(null)

  const hosts = useInventory(tenant)
  const drift = useDrift({ tenant, ...(severity ? { severity } : {}) })
  const acknowledge = useAcknowledgeDrift(tenant)

  const canTriage = session?.memberships.some(
    (membership) => membership.tenant === tenant && membership.can_triage,
  )

  if (hosts.isError) return <Failure error={hosts.error} onRetry={() => hosts.refetch()} />

  return (
    <section aria-labelledby="inventory-heading">
      <header
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h2 id="inventory-heading" style={{ margin: 0 }}>
          Inventory
        </h2>
        <span className="muted">
          {hosts.data ? hosts.data.results.length : 0} host(s) reporting · collected hourly by the
          agent
        </span>
      </header>

      <section aria-labelledby="drift-heading" style={{ marginTop: 'var(--space-5)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <h3 id="drift-heading" style={{ margin: 0 }}>
            Unacknowledged changes
          </h3>
          <label htmlFor="drift-severity" className="muted">
            Severity
          </label>
          <select
            id="drift-severity"
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
          >
            <option value="">any</option>
            <option value="critical">critical</option>
            <option value="notable">notable</option>
            <option value="info">informational</option>
          </select>
        </div>

        {drift.isError ? (
          <Failure error={drift.error} onRetry={() => drift.refetch()} />
        ) : drift.data && drift.data.results.length === 0 ? (
          <p className="panel" style={{ marginTop: 'var(--space-3)' }} role="status">
            Nothing unacknowledged. That means every change the agents reported has been looked
            at — not that nothing has changed.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 'var(--space-3) 0 0', padding: 0 }}>
            {(drift.data?.results ?? []).map((row) => (
              <li key={row.id} style={{ marginBottom: 'var(--space-2)' }}>
                <Change
                  row={row}
                  canTriage={Boolean(canTriage)}
                  onAcknowledge={() => acknowledge.mutate({ id: row.id })}
                  pending={acknowledge.isPending}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="hosts-heading" style={{ marginTop: 'var(--space-5)' }}>
        <h3 id="hosts-heading">Hosts</h3>
        {!hosts.data ? (
          <div aria-hidden="true">
            {Array.from({ length: 4 }, (_, index) => (
              <div className="skeleton" key={index} />
            ))}
          </div>
        ) : hosts.data.results.length === 0 ? (
          <p className="empty">
            No agent has reported an inventory yet. An enrolled agent collects one within the
            hour; a host that never does is a host this screen cannot describe.
          </p>
        ) : (
          hosts.data.results.map((host) => (
            <Host
              key={host.hostname}
              host={host}
              tenant={tenant}
              open={open?.hostname === host.hostname ? open.facet : null}
              onOpen={(facet) =>
                setOpen((current) =>
                  current?.hostname === host.hostname && current.facet === facet
                    ? null
                    : { hostname: host.hostname, facet },
                )
              }
            />
          ))
        )}
      </section>
    </section>
  )
}

function Change({
  row,
  canTriage,
  onAcknowledge,
  pending,
}: {
  row: DriftRow
  canTriage: boolean
  onAcknowledge: () => void
  pending: boolean
}) {
  const severity = SEVERITY[row.severity]
  return (
    <div
      className="panel"
      style={{ borderLeft: `3px solid ${severity.colour}`, padding: 'var(--space-3)' }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}>
        {/* The state is in a word as well as a colour, for the same reason severity
            is everywhere else in this console. */}
        <span style={{ color: severity.colour, fontSize: 'var(--text-xs)' }}>{severity.word}</span>
        <span className="mono">{row.hostname}</span>
        <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          {FACET_NAMES[row.facet] ?? row.facet} · {row.change}
        </span>
        {canTriage && (
          <button style={{ marginLeft: 'auto' }} onClick={onAcknowledge} disabled={pending}>
            This was expected
          </button>
        )}
      </div>
      <p className="mono" style={{ margin: 'var(--space-2) 0 0' }}>
        {row.item_key}
      </p>
      {/* Both values, always. A diff that only shows the new value cannot be read
          after the item changes again. */}
      <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
        {row.previous_value || '—'} → {row.current_value || '—'}
      </p>
      <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-xs)' }}>{row.reason}</p>
      <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
        observed {row.observed_at ? new Date(row.observed_at).toLocaleString() : 'unknown'}
      </p>
    </div>
  )
}

function Host({
  host,
  tenant,
  open,
  onOpen,
}: {
  host: InventoryHost
  tenant: string
  open: string | null
  onOpen: (facet: string) => void
}) {
  return (
    <section className="panel" style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}>
        <h4 className="mono" style={{ margin: 0 }}>
          {host.hostname}
        </h4>
        <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          last collected{' '}
          {host.last_collected_at ? new Date(host.last_collected_at).toLocaleString() : 'never'}
          {host.last_changed_at
            ? ` · last changed ${new Date(host.last_changed_at).toLocaleString()}`
            : ' · nothing has changed since we first looked'}
        </span>
        {host.unreadable > 0 && (
          <span className="badge" style={{ color: 'var(--warn)' }}>
            {host.unreadable} unreadable
          </span>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          marginTop: 'var(--space-3)',
        }}
      >
        {host.facets.map((facet) => (
          <FacetButton
            key={facet.facet}
            facet={facet}
            expanded={open === facet.facet}
            onClick={() => onOpen(facet.facet)}
          />
        ))}
      </div>

      {open && <Items hostname={host.hostname} facet={open} tenant={tenant} />}
    </section>
  )
}

function FacetButton({
  facet,
  expanded,
  onClick,
}: {
  facet: InventoryFacet
  expanded: boolean
  onClick: () => void
}) {
  const name = FACET_NAMES[facet.facet] ?? facet.facet
  // An unreadable facet is not a button to an empty list. It says what happened, and
  // is not clickable, because there is nothing behind it to look at.
  if (!facet.supported) {
    return (
      <span
        className="badge"
        style={{ color: 'var(--warn)' }}
        title={facet.error}
      >
        {name}: unreadable
      </span>
    )
  }
  return (
    <button aria-expanded={expanded} onClick={onClick}>
      {name} <span className="muted">{facet.item_count}</span>
      {facet.stale && <span style={{ color: 'var(--warn)' }}> · stale</span>}
      {facet.truncated && <span style={{ color: 'var(--warn)' }}> · truncated</span>}
    </button>
  )
}

function Items({
  hostname,
  facet,
  tenant,
}: {
  hostname: string
  facet: string
  tenant: string
}) {
  const [needle, setNeedle] = useState('')
  const query = useInventoryFacet(hostname, facet, { tenant, ...(needle ? { q: needle } : {}) })

  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <label htmlFor={`filter-${hostname}-${facet}`} className="muted">
        Filter {FACET_NAMES[facet] ?? facet}
      </label>{' '}
      <input
        id={`filter-${hostname}-${facet}`}
        value={needle}
        onChange={(event) => setNeedle(event.target.value)}
        placeholder="openssh"
      />

      {query.isError ? (
        <Failure error={query.error} onRetry={() => query.refetch()} />
      ) : !query.data ? (
        <div className="skeleton" aria-hidden="true" />
      ) : (
        <>
          <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
            {query.data.matched} of {query.data.item_count} shown
            {query.data.matched > query.data.items.length
              ? ` · first ${query.data.items.length}`
              : ''}{' '}
            · from {query.data.source}
          </p>
          <table>
            <caption className="visually-hidden">
              {FACET_NAMES[facet] ?? facet} on {hostname}
            </caption>
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((item) => (
                <tr key={item.key}>
                  <td className="mono">{item.key}</td>
                  <td className="mono">{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
